import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useTheme } from "@mui/material"

import type { LinePlotView, RecordType } from "types/dashboard.types"
import type { FieldQualityMap } from "types/quality.types"

import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import useDateRange from "helpers/hooks/useDateRange"
import muiLineConfig from "./muiLine"

interface useMuiLineConfigProps {
  view: LinePlotView
  data: RecordType[]
  tooltipData: RecordType[]
  fromDate?: number
  toDate?: number
  fieldQualityMap?: FieldQualityMap
}

const useMuiLineConfig = ({
  view,
  data,
  tooltipData,
  fromDate,
  toDate,
  fieldQualityMap,
}: useMuiLineConfigProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { i18n } = useTranslation()
  const locale = i18n.resolvedLanguage

  const { entityType } = useDashboardContext()
  const { getDateRangeTools } = useDateRange()
  const { dateRange } = useMemo(
    () => getDateRangeTools(entityType ?? "device"),
    [getDateRangeTools, entityType],
  )

  const fromDateMillis = useMemo(
    () =>
      fromDate !== undefined
        ? fromDate
        : dateRange?.fromDate
          ? new Date(dateRange.fromDate).getTime()
          : NaN,
    [fromDate, dateRange?.fromDate],
  )
  const toDateMillis = useMemo(
    () =>
      toDate !== undefined
        ? toDate
        : dateRange?.toDate
          ? new Date(dateRange.toDate).getTime()
          : NaN,
    [toDate, dateRange?.toDate],
  )

  const graphConfig = useMemo(
    () =>
      muiLineConfig({
        view,
        data,
        tooltipData,
        palette: theme.palette,
        t,
        locale,
        fromDate: fromDateMillis,
        toDate: toDateMillis,
        fieldQualityMap,
      }),
    [
      view,
      data,
      tooltipData,
      theme.palette,
      t,
      locale,
      fromDateMillis,
      toDateMillis,
      fieldQualityMap,
    ],
  )

  return graphConfig
}

export default useMuiLineConfig
